import React, { useEffect, useState } from "react";
import ReactTooltip from 'react-tooltip';
import { fetchData } from "../utils/api";
import { deleteData } from "../utils/api";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useFeatureGates from "../hooks/useFeatureGates";

import dollarIcon from '../../../assets/images/performance/doller-icon.svg';
import personIcon from '../../../assets/images/performance/person-star.svg';
import checkIcon from '../../../assets/images/performance/eye-check.svg';
import chartIcon from '../../../assets/images/performance/chart.svg'
import ArrowDown from '../../../assets/images/performance/arrow-down.svg'
import ArrowUp from '../../../assets/images/performance/arrow-up.svg'
import Loader from "../components/Loader";

const EventsListing = () => {
  const queryClient = useQueryClient();
  const { featureGates } = useFeatureGates('company');
  const hasAttendeeValueEstimator = featureGates.includes('true-attendee-value-estimator');

  // Get current page from URL or default to 1
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPageFromUrl = parseInt(searchParams.get("page") || 1, 10);
  const [currentPage, setCurrentPage] = useState(currentPageFromUrl);

  const defaultTab = searchParams.get("event_status") || "upcoming";
  const [activeTab, setActiveTab] = useState(defaultTab);

  const sortColumn = searchParams.get("sort") || 'start_date';
  const sortDirection = searchParams.get("direction") || 'desc' ;
  const searchFromUrl = searchParams.get("search") || "";
  const marketIdFromUrl = searchParams.get("market_id") || "";
  const cityFromUrl = searchParams.get("city") || "";
  const stateFromUrl = searchParams.get("state") || "";
  const startDateFromUrl = searchParams.get("start_date") || "";
  const endDateFromUrl = searchParams.get("end_date") || "";

  const [searchText, setSearchText] = useState(searchFromUrl);
  const [marketId, setMarketId] = useState(marketIdFromUrl);
  const [cityText, setCityText] = useState(cityFromUrl);
  const [stateText, setStateText] = useState(stateFromUrl);
  const [startDate, setStartDate] = useState(startDateFromUrl);
  const [endDate, setEndDate] = useState(endDateFromUrl);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    Boolean(marketIdFromUrl || cityFromUrl || stateFromUrl || startDateFromUrl || endDateFromUrl)
  );

  useEffect(() => {
    setSearchText(searchFromUrl);
    setMarketId(marketIdFromUrl);
    setCityText(cityFromUrl);
    setStateText(stateFromUrl);
    setStartDate(startDateFromUrl);
    setEndDate(endDateFromUrl);
  }, [searchFromUrl, marketIdFromUrl, cityFromUrl, stateFromUrl, startDateFromUrl, endDateFromUrl]);

  useEffect(() => {
    // Auto-open advanced filters if user already has any applied via URL.
    const hasAdvanced = Boolean(marketIdFromUrl || cityFromUrl || stateFromUrl || startDateFromUrl || endDateFromUrl);
    if (hasAdvanced) setShowAdvancedFilters(true);
  }, [marketIdFromUrl, cityFromUrl, stateFromUrl, startDateFromUrl, endDateFromUrl]);

  const queryParams = new URLSearchParams({
    page: currentPage,
    per_page: 50,
    event_status: activeTab
  });

  if (sortColumn) queryParams.append("sort", sortColumn);
  if (sortDirection) queryParams.append("direction", sortDirection);
  if (searchFromUrl) queryParams.append("search", searchFromUrl);
  if (marketIdFromUrl) queryParams.append("market_id", marketIdFromUrl);
  if (cityFromUrl) queryParams.append("city", cityFromUrl);
  if (stateFromUrl) queryParams.append("state", stateFromUrl);
  if (startDateFromUrl) queryParams.append("start_date", startDateFromUrl);
  if (endDateFromUrl) queryParams.append("end_date", endDateFromUrl);

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["events", currentPage, sortColumn, sortDirection, activeTab, searchFromUrl, marketIdFromUrl, cityFromUrl, stateFromUrl, startDateFromUrl, endDateFromUrl],
    queryFn: () =>
      fetchData(`api/a/events?${queryParams.toString()}`),
    keepPreviousData: true,
  });

  // Match the homepage "Find Events" datepicker UX (bootstrap-datepicker).
  // React mounts after Turbolinks load, so initialize here as well.
  useEffect(() => {
    const $ = window.$;
    if (!$ || !$.fn || !$.fn.datepicker) return;
    if (isLoading) return;

    const wire = (selector, setter) => {
      const $el = $(selector);
      if (!$el.length) return;
      if ($el.data('datepicker')) return;

      $el.datepicker({
        todayHighlight: true,
        autoclose: true,
        format: 'mm/dd/yyyy',
        container: 'body',
        zIndexOffset: 99999,
      });
      $el.on("changeDate", function () {
        setter($(this).val());
      });
    };

    wire("#company-events-start-date", setStartDate);
    wire("#company-events-end-date", setEndDate);
  }, [isLoading, data]);

  const showDatepicker = (inputId, setter) => {
    const $ = window.$;
    if (!$ || !$.fn || !$.fn.datepicker) return;
    const $el = $(`#${inputId}`);
    if (!$el.length) return;

    // Ensure it is initialized (covers edge cases with load order)
    if (!$el.data('datepicker')) {
      $el.datepicker({
        todayHighlight: true,
        autoclose: true,
        format: 'mm/dd/yyyy',
        container: 'body',
        zIndexOffset: 99999,
      });
    }

    // Ensure the change handler is always wired (namespaced to avoid duplicates)
    $el.off("changeDate.companyEvents").on("changeDate.companyEvents", function () {
      if (typeof setter === "function") setter($(this).val());
    });

    // Defer to allow React focus to settle
    setTimeout(() => {
      try {
        $el.datepicker('show');
      } catch (_e) {
        // no-op
      }
    }, 0);
  };

  const handleSort = (column) => {
    const isSame = sortColumn === column;
    const newDirection = isSame && sortDirection === "asc" ? "desc" : "asc";
    const params = Object.fromEntries(searchParams.entries());
    setCurrentPage(1);
    setSearchParams({ ...params, page: 1, sort: column, direction: newDirection });
  };

  const handleTabChange = (tab) => {
     setActiveTab(tab);
     setCurrentPage(1)
     const params = Object.fromEntries(searchParams.entries());
     setSearchParams({ ...params, event_status: tab, page: 1 });
  }

  const handlePageChange = (newPage) => {
    const params = Object.fromEntries(searchParams.entries());
    setCurrentPage(newPage);
    setSearchParams({ ...params, page: newPage });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = Object.fromEntries(searchParams.entries());
    const next = { ...params, page: 1 };

    const trimmedSearch = searchText.trim();
    const trimmedCity = cityText.trim();
    const trimmedState = stateText.trim();

    if (trimmedSearch) next.search = trimmedSearch;
    else delete next.search;

    if (marketId) next.market_id = marketId;
    else delete next.market_id;

    if (trimmedCity) next.city = trimmedCity;
    else delete next.city;

    if (trimmedState) next.state = trimmedState;
    else delete next.state;

    if (startDate) next.start_date = startDate;
    else delete next.start_date;

    if (endDate) next.end_date = endDate;
    else delete next.end_date;

    setCurrentPage(1);
    setSearchParams(next);
  };

  // Only block the UI on the very first load; during refetches (search/sort/tab/page)
  // keep the table visible to avoid the "blink" effect. `isLoading` alone is not enough to
  // guarantee `data` exists: react-query v5 defines it as `isPending && isFetching`, so an
  // offline/paused query (status 'pending', fetchStatus 'paused') reports isLoading=false with
  // error=null and data=undefined — and the body below dereferences data.events/data.total_pages
  // unguarded. `!data` holds the loader on that path. Error is checked first so a failed query
  // still surfaces its message instead of spinning forever (same order as
  // AdminAttendeeValueOverridesPage).
  if (error)
    return (
      <div className="error-container">
        <p>Failed to load events.</p>
      </div>
    );

  if (isLoading || !data) return <Loader />;


  const handleDelete = async (eventId) => {
    const isConfirmed = window.confirm('Are you sure you want to delete this event?');
    if (!isConfirmed) return;

    try {
      await deleteData(`api/events/${eventId}`);
      queryClient.invalidateQueries(["events", currentPage, sortColumn, sortDirection]);
    } catch (error) {
      console.error("Failed to delete event:", error);
    }
  };

  const renderPagination = () => (
    <div className="d-flex justify-content-end align-items-center mt-4 mb-3">
      <div className="btn-group" role="group">
        <button
          className="btn pagination-btn px-4 rounded-pill "
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        <div className="dropdown mx-2">
          <button
            className="pagination-btn px-4 dropdown-toggle custom-pagination-btn rounded-pill "
            type="button"
            id="pageDropdown"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Page {currentPage}
          </button>
          <div className="dropdown-menu dropdown-menu-scrollable" aria-labelledby="pageDropdown">
            {Array.from({ length: data.total_pages }, (_, i) => (
              <button
                key={i + 1}
                className={`dropdown-item btn btn-pagination rounded-pill  ${currentPage === i + 1 ? 'active' : ''}`}
                type="button"
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>

        <button
          className="btn pagination-btn rounded-pill px-4"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === data.total_pages}
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <div>
         {/* Tab Navigation  */}
          <div className="d-flex align-items-center justify-content-between mt-3 pb-3 overflow-auto">
            <div className="tab-buttons bg-light custom-shadow d-flex px-4 events-tabs justify-content-between">
              {["upcoming", "past", "draft"].map((tab) => (
                <button
                  key={tab}
                  className={`btn rounded-pill event-tabs-btn ${
                    activeTab === tab
                      ? "primary-purple text-white font-weight-bold"
                      : "bg-transparent text-dark font-weight-normal"
                  }`}
                  style={{
                    border: "none",
                    fontWeight: "500",
                  }}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

          {data.events.length > 0 && (
            <div className="pagination-controls d-flex gap-2 align-items-center">
              {renderPagination()}
            </div>
          )}
        </div>

        <form onSubmit={handleSearchSubmit} className="formatlabels mb-3">
          {/* Line 1: Event name + actions */}
          <div className="row-layout" style={{ gap: "0.75rem", alignItems: "center" }}>
            <div className="row-item" style={{ flex: "1 1 520px" }}>
              <div className="form-group">
                <label className="mt-2">Event Name:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search events by name"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            <div
              className="row-item company-events-search-actions"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.5rem",
                alignItems: "center",
                marginTop: "10px",
                flex: "0 1 auto",
              }}
            >
              <button
                type="submit"
                className="btn rounded-pill px-4"
                style={{ border: "none", height: "38px", backgroundColor: "#633AFB", color: "#fff" }}
              >
                SEARCH
              </button>
              <button
                id="searchoptions-btn"
                type="button"
                className="btn advanced-filters-toggle"
                onClick={() => setShowAdvancedFilters((v) => !v)}
              >
                Advanced{" "}
                <i className={`fa fa-caret-square-${showAdvancedFilters ? "up" : "down"}`} />
              </button>
              {isFetching && (
                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Updating…
                </span>
              )}
            </div>
          </div>

          {showAdvancedFilters && (
            <div id="searchoptions-events-bin" style={{ marginTop: "0.5rem", display: "block" }}>
              {/* Advanced row 1: Market + City + State */}
              <div className="row-layout" style={{ gap: "0.75rem" }}>
                <div className="row-item" style={{ flex: "0 0 260px" }}>
                  <div className="form-group">
                    <label className="mt-2">Market:</label>
                    <select
                      className="form-control"
                      value={marketId}
                      onChange={(e) => setMarketId(e.target.value)}
                    >
                      <option value="">All markets</option>
                      {(data?.markets || []).map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row-item" style={{ flex: "1 1 420px" }}>
                  <div className="row-layout" style={{ gap: "0.75rem" }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="mt-2">City:</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="City (e.g., Omaha)"
                        value={cityText}
                        onChange={(e) => setCityText(e.target.value)}
                        id="web-city-search"
                      />
                    </div>

                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="mt-2">State:</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="State (e.g., NE)"
                        value={stateText}
                        onChange={(e) => setStateText(e.target.value)}
                        id="web-state-search"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced row 2: Date range */}
              <div className="row-layout" style={{ gap: "0.75rem" }}>
                <div className="row-item" style={{ flex: "1 1 520px" }}>
                  <div className="form-group" id="dateSearch">
                    <label className="mt-2">Date Range:</label>
                    <div className="row-layout" style={{ gap: "0.75rem" }}>
                      <div className="form-group" style={{ flex: 1 }}>
                        <input
                          id="company-events-start-date"
                          type="text"
                          className="datepicker form-control min40 formfield-style company-events-datepicker"
                          placeholder="Start Date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          onFocus={() => showDatepicker('company-events-start-date', setStartDate)}
                          onClick={() => showDatepicker('company-events-start-date', setStartDate)}
                        />
                      </div>
                      <div className="form-group" style={{ flex: 1 }}>
                        <input
                          id="company-events-end-date"
                          type="text"
                          className="datepicker form-control min40 formfield-style company-events-datepicker"
                          placeholder="End Date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          onFocus={() => showDatepicker('company-events-end-date', setEndDate)}
                          onClick={() => showDatepicker('company-events-end-date', setEndDate)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

        <div className="p-3 bg-white shadow-sm rounded-10" style={{ border: "1px solid #ddd" }}>
          <h2 className="mb-3 mt-4 font-size-22">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Events</h2>

          {data.events.length === 0 ? (
            <div className="text-muted" style={{ padding: "1.25rem 0" }}>
              No events found. Try adjusting your filters above.
            </div>
          ) : (
            <div className="table-responsive table-wrapper">
              <table className="table bg-white table-container">
                <thead className="table-header-bold">
                  <tr className="font-size-13">
                    <th className="border-0 events-th ">Event Details</th>
                    <th onClick={() => handleSort("start_date")} className="border-0 events-th cursor-pointer">
                      Starts{" "}
                      <span className={sortColumn === "start_date" ? "" : "text-muted"}>
                        {sortDirection === "asc" && sortColumn === "start_date" ? <img className="h-25 w-25 mb-1 arrow" src={ArrowUp} /> : <img className="h-25 w-25 mb-1 arrow" src={ArrowDown} />}
                      </span>
                    </th>

                    <th onClick={() => handleSort("end_date")} className="border-0 events-th cursor-pointer">
                      Ends{" "}
                      <span className={sortColumn === "end_date" ? "" : "text-muted"}>
                        {sortDirection === "asc" && sortColumn === "end_date" ? <img className="h-25 w-25 mb-1 arrow" src={ArrowDown} /> : <img className="h-25 w-25 mb-1 arrow" src={ArrowUp} />}
                      </span>
                    </th>
                    <th className="border-0 events-th">Estimated ROI</th>
                    <th className="border-0 events-th">Estimated Sales</th>
                    <th className="border-0 events-th">Ticket Page Visitors</th>
                    <th className="border-0 events-th">Estimated Views</th>
                    <th className="border-0 events-th">Boost</th>
                  </tr>
                </thead>
                <tbody className="table-data">
                  {data.events.map((event) => (
                    <tr key={event.id}>
                      <td className="manrope-semibold font-size-13">
                        <a
                          href={`/events/${event.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none text-dark set-min-width"
                        >
                          <div className="d-flex align-items-center">
                            <img
                              src={event.banner_image_url}
                              alt="event"
                              className="img-thumbnail p-0 rounded-10"
                              style={{ width: "90px", height: "50px" }}
                            />
                            <div className="ml-2">
                              <div className="events-title d-inline-flex">
                                <div className="event-title-text">
                                  <i className="fa fa-external-link mr-2 font_size-10 m-bottom-3" aria-hidden="true"></i>
                                  {event.name}
                                </div>
                              </div>
                              <div className="events-sub-title">
                                {event.city_state} | {event.start_date}
                              </div>
                            </div>
                          </div>
                        </a>
                      </td>
                      <td className="events-td-12">{event.start_date}</td>
                      <td className="events-td-12">{event.end_date}</td>
                      <td className="border-top events-td-12" data-for={`event-roi-{event.id}`} data-tip="ROI isn't displayed until the day an event ends">
                        {event.is_upcoming && (
                          <ReactTooltip
                            id={`event-roi-{event.id}`}
                            place="bottom"
                            effect="solid"
                            className="tooltip-lg"
                          />
                        )}
                        <img src={chartIcon} className="pr-1" alt="chart icon" />
                        {typeof event.estimated_roi === 'number' ? event.estimated_roi.toLocaleString('en-US') + 'x' : event.estimated_roi}
                        {hasAttendeeValueEstimator && (
                          <Link
                            to={`/companies/${event.company_slug || ''}/events/${event.id}/analytics`}
                            className="ml-2 text-primary"
                            title="View detailed analytics"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fa fa-chart-bar" style={{ fontSize: "0.85rem" }}></i>
                          </Link>
                        )}
                      </td>
                      <td className="border-top events-td-12">
                        <img src={dollarIcon} className="pr-1" alt="event" />
                        {event.estimated_sales}
                      </td>
                      <td className="border-top events-td-12">
                        <img src={personIcon} className="pr-1" alt="event" />
                        {event.ticket_page_visits}
                      </td>
                      <td className="border-top events-td-12">
                        <img src={checkIcon} className="pr-1" alt="event" />
                        {event.estimated_views}
                      </td>
                      <td className="border-top">
                        <div className="d-flex align-items-center">
                          <Link
                            to={`/marketing?event_id=${event.id}`}
                            className={`event-boost-btn d-flex align-items-center ${!event.can_boost && 'pointer-events-none cursor-not-allowed opacity-50'}`}
                          >
                            <span className="text-nowrap">Boost Event</span>
                            <i className="fa-solid fa-arrow-right ml-2" aria-hidden="true"></i>
                          </Link>
                          <Link
                            to={`/events/${event.id}/analytics`}
                            className="event-performance-btn d-flex align-items-center"
                            title="Performance"
                          >
                            <i className="fa-solid fa-chart-line mr-1" aria-hidden="true"></i>
                            <span className="text-nowrap">Performance</span>
                          </Link>
                        </div>
                      </td>
                      {!event.published && (
                        <td className="border-top events-td-12">
                          <i
                            className="fa fa-trash text-danger cursor-pointer mt-2"
                            onClick={() => handleDelete(event.id)}
                            aria-label="Delete event"
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )}
        </div>

        {data.events.length > 0 && renderPagination()}
      </div>
    </div>
  );
};

export default EventsListing;
